import React, { useCallback, useEffect, useState } from 'react';
import { 
  Box, 
  Alert, 
  Button, 
  Typography,
  Fade,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { macroForecastAPI } from '../services/api';
import {
  ForecastScenarioCards,
  ScenarioComparisonChart
} from '../components/forecast';
import MacroForecastSkeleton from '../components/forecast/MacroForecastSkeleton';

const MacroForecast = () => {
  useTranslation();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [forecasts, setForecasts] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [horizon, setHorizon] = useState('1Y');
  const [error, setError] = useState(null);

  const fetchTargets = useCallback(async () => {
    try {
      const response = await macroForecastAPI.getTargets();
      setTargets(response.data.targets);
      if (response.data.targets.length > 0) {
        setSelectedTarget(response.data.targets[0].target_id);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch targets:', err);
      setError('Failed to load forecast targets');
      setLoading(false);
    }
  }, []);

  const generateForecast = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await macroForecastAPI.generateForecast({
        target_id: selectedTarget,
        horizons: ['1M', '3M', '6M', '1Y'],
        method: 'KALMAN',
        include_shock: true,
        include_scenarios: true
      });
      
      // Convert generated forecast to format expected by components
      const forecastData = [];
      
      // Add base forecast
      if (response.data.forecasts) {
        Object.entries(response.data.forecasts).forEach(([horizon, data]) => {
          forecastData.push({
            forecast_id: `generated_${selectedTarget}_${horizon}`,
            scenario: 'BASE',
            horizon: horizon,
            mean: data.mean,
            p10: data.lower,
            p50: data.mean,
            p90: data.upper,
            probability: 0.5
          });
        });
      }
      
      // Add scenarios if available
      if (response.data.scenarios) {
        Object.entries(response.data.scenarios).forEach(([scenarioType, scenarioForecasts]) => {
          Object.entries(scenarioForecasts).forEach(([horizon, data]) => {
            forecastData.push({
              forecast_id: `generated_${selectedTarget}_${scenarioType}_${horizon}`,
              scenario: scenarioType,
              horizon: horizon,
              mean: data.mean,
              p10: data.lower,
              p50: data.mean,
              p90: data.upper,
              probability: scenarioType === 'BASE' ? 0.5 : 0.25
            });
          });
        });
      }
      
      setForecasts(forecastData);
      setGenerating(false);
      
    } catch (err) {
      console.error('Failed to generate forecast:', err);
      setError('Failed to generate forecast');
      setGenerating(false);
    }
  }, [selectedTarget]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  useEffect(() => {
    if (selectedTarget) {
      generateForecast();
    }
  }, [generateForecast, selectedTarget]);

  // Loading State
  if (loading && !selectedTarget) {
    return <MacroForecastSkeleton />;
  }

  const targetName = targets.find(t => t.target_id === selectedTarget)?.target_name || 'Target';

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Macro Forecasting
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Multi-horizon forecasts with scenario analysis
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Target Variable</InputLabel>
          <Select
            value={selectedTarget || ''}
            label="Target Variable"
            onChange={(e) => setSelectedTarget(Number(e.target.value))}
          >
            {targets.map(t => (
              <MenuItem key={t.target_id} value={t.target_id}>
                {t.target_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Horizon</InputLabel>
          <Select
            value={horizon}
            label="Horizon"
            onChange={(e) => setHorizon(e.target.value)}
          >
            <MenuItem value="1M">1 Month</MenuItem>
            <MenuItem value="3M">3 Months</MenuItem>
            <MenuItem value="6M">6 Months</MenuItem>
            <MenuItem value="1Y">1 Year</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={generateForecast}
          disabled={generating || loading}
          sx={{ 
            px: 3, 
            py: 1.5,
            textTransform: 'none'
          }}
        >
          {generating ? 'Generating...' : 'Generate Forecast'}
        </Button>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      {!loading && forecasts.length > 0 && (
        <Fade in timeout={600}>
          <Box>
            {/* Scenario Cards */}
            <ForecastScenarioCards 
              forecasts={forecasts}
              targetName={targetName}
              horizon={horizon}
            />

            {/* Scenario Chart */}
            <ScenarioComparisonChart 
              forecasts={forecasts}
              targetName={targetName}
              horizon={horizon}
            />

          </Box>
        </Fade>
      )}

      {/* Empty State */}
      {!loading && forecasts.length === 0 && selectedTarget && !error && (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            px: 3
            // background and border removed
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            No Forecast Data Available
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Click "Generate Forecast" to create forecasts for <strong>{targetName}</strong> with {horizon} horizon.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will analyze historical data and generate BASE, UPSIDE, and DOWNSIDE scenarios.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MacroForecast;
